import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { Plus, Edit2, Trash2, Clock, User, Check, X, BookOpen, TrendingUp, ArrowUpRight, GraduationCap, Users, Calendar, Search, Eye, UserPlus, UserMinus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import StudentAvatar from '../components/StudentAvatar';

const ClassManagement = () => {
  const { user } = useContext(AuthContext);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    time_slot: '',
    teacher_name: '',
    description: '',
    preparation_type: '',
    status: 'Active'
  });

  const [showLessonModal, setShowLessonModal] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [activeClassForLesson, setActiveClassForLesson] = useState(null);
  const [lessonSearchQuery, setLessonSearchQuery] = useState('');
  const [showLessonFormModal, setShowLessonFormModal] = useState(false);
  const [showLessonDetailModal, setShowLessonDetailModal] = useState(false);
  const [selectedLessonForDetail, setSelectedLessonForDetail] = useState(null);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [lessonFormPayload, setLessonFormPayload] = useState({
    lesson_date: new Date().toISOString().split('T')[0],
    content: ''
  });
  const [savingLesson, setSavingLesson] = useState(false);

  // Enroll Students Modal
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollClass, setEnrollClass] = useState(null);
  const [eligibleStudents, setEligibleStudents] = useState([]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [selectedToEnroll, setSelectedToEnroll] = useState([]);
  const [enrollLoading, setEnrollLoading] = useState(false);

  const openEnrollModal = async (cls) => {
    setEnrollClass(cls);
    setSelectedToEnroll([]);
    setEnrollLoading(true);
    setShowEnrollModal(true);
    try {
      const [eligibleRes, enrolledRes] = await Promise.all([
        api.get(`/classes/${cls.id}/eligible-students`),
        api.get(`/classes/${cls.id}/students`)
      ]);
      setEligibleStudents(eligibleRes.data);
      setEnrolledStudents(enrolledRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setEnrollLoading(false);
    }
  };

  const handleEnrollSubmit = async () => {
    if (selectedToEnroll.length === 0) return;
    try {
      await api.post(`/classes/${enrollClass.id}/students`, { studentIds: selectedToEnroll });
      const [eligibleRes, enrolledRes] = await Promise.all([
        api.get(`/classes/${enrollClass.id}/eligible-students`),
        api.get(`/classes/${enrollClass.id}/students`)
      ]);
      setEligibleStudents(eligibleRes.data);
      setEnrolledStudents(enrolledRes.data);
      setSelectedToEnroll([]);
      fetchClasses();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to enroll students');
    }
  };

  const handleRemoveFromClass = async (studentId) => {
    if (!window.confirm('Remove this student from the class?')) return;
    try {
      await api.delete(`/classes/${enrollClass.id}/students/${studentId}`);
      const [eligibleRes, enrolledRes] = await Promise.all([
        api.get(`/classes/${enrollClass.id}/eligible-students`),
        api.get(`/classes/${enrollClass.id}/students`)
      ]);
      setEligibleStudents(eligibleRes.data);
      setEnrolledStudents(enrolledRes.data);
      fetchClasses();
    } catch (err) {
      alert('Failed to remove student');
    }
  };

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const res = await api.get('/classes');
      setClasses(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClass) {
        await api.put(`/classes/${editingClass.id}`, formData);
      } else {
        await api.post('/classes', formData);
      }
      setShowModal(false);
      setEditingClass(null);
      setFormData({ name: '', time_slot: '', teacher_name: '', description: '', preparation_type: '', status: 'Active' });
      fetchClasses();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error saving class';
      alert(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await api.delete(`/classes/${id}`);
      fetchClasses();
    } catch (err) {
      alert('Error deleting class');
    }
  };

  const fetchLessons = async (classId) => {
    try {
      const res = await api.get(`/classes/${classId}/lessons`);
      setLessons(res.data);
    } catch (err) {
      console.error('Error fetching lessons:', err);
    }
  };

  const openLessonModal = (cls) => {
    setActiveClassForLesson(cls);
    setLessonSearchQuery('');
    fetchLessons(cls.id);
    setShowLessonModal(true);
  };

  const handleLessonSave = async (e) => {
    e.preventDefault();
    if (!lessonFormPayload.content) return;
    setSavingLesson(true);
    try {
      if (editingLessonId) {
        await api.put(`/classes/${activeClassForLesson.id}/lessons/${editingLessonId}`, {
          lesson_date: lessonFormPayload.lesson_date,
          content: lessonFormPayload.content
        });
      } else {
        await api.post(`/classes/${activeClassForLesson.id}/lessons`, {
          lesson_date: lessonFormPayload.lesson_date,
          content: lessonFormPayload.content
        });
      }
      setShowLessonFormModal(false);
      fetchLessons(activeClassForLesson.id);
    } catch (err) {
      console.error('Error saving lesson log:', err);
      alert('Error saving lesson log');
    } finally {
      setSavingLesson(false);
    }
  };

  const handleLessonDelete = async (lessonId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this lesson log?')) return;
    try {
      await api.delete(`/classes/${activeClassForLesson.id}/lessons/${lessonId}`);
      fetchLessons(activeClassForLesson.id);
      if (selectedLessonForDetail?.id === lessonId) {
        setShowLessonDetailModal(false);
      }
    } catch (err) {
      console.error('Error deleting lesson log:', err);
      alert('Error deleting lesson log');
    }
  };

  const handleLessonEditClick = (lesson, e) => {
    e.stopPropagation();
    setEditingLessonId(lesson.id);
    const formattedDate = new Date(lesson.lesson_date).toISOString().split('T')[0];
    setLessonFormPayload({
      lesson_date: formattedDate,
      content: lesson.content
    });
    setShowLessonFormModal(true);
  };

  const handleLessonAddClick = () => {
    setEditingLessonId(null);
    setLessonFormPayload({
      lesson_date: new Date().toISOString().split('T')[0],
      content: ''
    });
    setShowLessonFormModal(true);
  };

  const handleLessonRowClick = (lesson) => {
    setSelectedLessonForDetail(lesson);
    setShowLessonDetailModal(true);
  };

  const filteredLessons = useMemo(() => {
    return lessons.filter(lesson => {
      const query = lessonSearchQuery.toLowerCase();
      const dateStr = new Date(lesson.lesson_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toLowerCase();
      const content = lesson.content.toLowerCase();
      const creator = (lesson.creator_name || '').toLowerCase();
      return dateStr.includes(query) || content.includes(query) || creator.includes(query);
    });
  }, [lessons, lessonSearchQuery]);

  const openModal = (cls = null) => {
    if (cls) {
      setEditingClass(cls);
      setFormData({
        name: cls.name,
        time_slot: cls.time_slot || '',
        teacher_name: cls.teacher_name || '',
        description: cls.description || '',
        preparation_type: cls.preparation_type || '',
        status: cls.status || 'Active'
      });
    } else {
      setEditingClass(null);
      setFormData({ name: '', time_slot: '', teacher_name: '', description: '', preparation_type: '', status: 'Active' });
    }
    setShowModal(true);
  };

  if (loading) return <div className="p-8 text-center animate-pulse">Loading classes...</div>;

  return (
    <div className="animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-blue-900">Manage Classes</h1>
          <p className="text-gray-500 text-sm mt-1 uppercase tracking-widest font-bold">Schedule classes and teachers</p>
        </div>
        {user?.role === 'Admin' && (
          <button 
            onClick={() => openModal()}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 hover:bg-blue-700 transition-all shadow-lg shadow-blue-100"
          >
            <Plus size={20} />
            Add New Class
          </button>
        )}
      </div>
      
      {/* Class Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Total Classes */}
        <div className="bg-blue-600 p-5 rounded-[1.5rem] shadow-lg shadow-blue-100 flex flex-col justify-between h-40 relative overflow-hidden group hover:scale-[1.02] transition-all text-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white text-blue-600 rounded-xl flex items-center justify-center shadow-sm">
              <GraduationCap size={20} />
            </div>
            <span className="text-[10px] font-black opacity-80 uppercase tracking-widest">Total Classes</span>
          </div>
          <h4 className="text-3xl font-black">{classes.length}</h4>
          <div className="flex items-center gap-2 opacity-80 font-bold text-[10px] uppercase tracking-tighter">
            <ArrowUpRight size={14} />
            <span>+2 this month</span>
          </div>
        </div>
        
        {/* Active Classes */}
        <div className="bg-teal-500 p-5 rounded-[1.5rem] shadow-lg shadow-teal-100 flex flex-col justify-between h-40 relative overflow-hidden group hover:scale-[1.02] transition-all text-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white text-teal-600 rounded-xl flex items-center justify-center shadow-sm">
              <Check size={20} />
            </div>
            <span className="text-[10px] font-black opacity-80 uppercase tracking-widest">Active Classes</span>
          </div>
          <h4 className="text-3xl font-black">{classes.filter(c => c.status === 'Active').length}</h4>
          <div className="flex items-center gap-2 opacity-80 font-bold text-[10px] uppercase tracking-tighter">
            <TrendingUp size={14} />
            <span>Full engagement</span>
          </div>
        </div>

        {/* Total Instructors */}
        <div className="bg-violet-600 p-5 rounded-[1.5rem] shadow-lg shadow-violet-100 flex flex-col justify-between h-40 relative overflow-hidden group hover:scale-[1.02] transition-all text-white">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white text-violet-600 rounded-xl flex items-center justify-center shadow-sm">
              <Users size={20} />
            </div>
            <span className="text-[10px] font-black opacity-80 uppercase tracking-widest">Total Teachers</span>
          </div>
          <h4 className="text-3xl font-black">
            {new Set(classes.map(c => c.teacher_name).filter(Boolean)).size}
          </h4>
          <div className="flex items-center gap-2 opacity-80 font-bold text-[10px] uppercase tracking-tighter">
            <ArrowUpRight size={14} />
            <span>Staff Members</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {classes.map(cls => (
          <div key={cls.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all group">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-tighter ${cls.status === 'Active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                    {cls.status}
                  </span>
                  {cls.preparation_type && (
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-tighter bg-indigo-50 text-indigo-600">
                      {cls.preparation_type}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-extrabold text-gray-900 mt-1">{cls.name}</h3>
              </div>
              {user?.role === 'Admin' && (
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openModal(cls)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(cls.id)} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-gray-600 text-sm">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Clock size={16} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase leading-none">Time Slot</p>
                  <p className="font-bold">{cls.time_slot || 'Not set'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-gray-600 text-sm">
                <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                  <User size={16} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase leading-none">Teacher</p>
                  <p className="font-bold">{cls.teacher_name || 'Not assigned'}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-4 border-t border-gray-50">
              <div className="flex gap-2">
                <Link 
                  to={`/attendance/${cls.id}`}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-black hover:bg-blue-600 hover:text-white transition-all uppercase tracking-widest"
                >
                  <Calendar size={14} />
                  Attendance
                </Link>
                <button 
                  onClick={() => openLessonModal(cls)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-black hover:bg-indigo-600 hover:text-white transition-all uppercase tracking-widest"
                >
                  <BookOpen size={14} />
                  Logs
                </button>
              </div>
              {cls.preparation_type && (
                <button
                  onClick={() => openEnrollModal(cls)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-black hover:bg-emerald-600 hover:text-white transition-all uppercase tracking-widest"
                >
                  <UserPlus size={14} />
                  Enroll Students
                  {cls.student_count > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[9px] group-hover:bg-emerald-500 group-hover:text-white">
                      {cls.student_count} enrolled
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-blue-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden">
            <div className="bg-blue-600 p-6 text-white">
              <h2 className="text-xl font-bold">{editingClass ? 'Edit Class' : 'Create New Class'}</h2>
              <p className="text-blue-100 text-xs mt-1">Fill in the details below</p>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Class Name</label>
                <input 
                  type="text" 
                  required
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. Morning Batch A"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Linked Preparation Type</label>
                <select
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                  value={formData.preparation_type}
                  onChange={e => setFormData({...formData, preparation_type: e.target.value})}
                >
                  <option value="">None (General Class)</option>
                  <option value="Japanese">Japanese</option>
                  <option value="Korean">Korean</option>
                  <option value="IELTS">IELTS</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Time Slot</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                  value={formData.time_slot}
                  onChange={e => setFormData({...formData, time_slot: e.target.value})}
                  placeholder="e.g. 7:00 AM - 9:00 AM"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Teacher Name</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                  value={formData.teacher_name}
                  onChange={e => setFormData({...formData, teacher_name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Description</label>
                <textarea 
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                />
              </div>
              {editingClass && (
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Status</label>
                  <select 
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                    value={formData.status}
                    onChange={e => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button 
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-500 font-bold rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-100"
                >
                  {editingClass ? 'Update Class' : 'Create Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showLessonModal && (
        <div className="fixed inset-0 bg-blue-900/40 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-3xl shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-gradient-to-r from-indigo-600 to-blue-700 p-8 text-white flex justify-between items-center shrink-0">
              <div>
                <h2 className="text-2xl font-black flex items-center gap-3">
                  <BookOpen size={24} />
                  Class Logs: {activeClassForLesson?.name}
                </h2>
                <p className="text-indigo-100 text-xs mt-1 font-bold uppercase tracking-widest">Daily records and lesson history</p>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={handleLessonAddClick}
                  className="px-4 py-2 bg-white text-indigo-700 hover:bg-indigo-50 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 shadow-md flex items-center gap-1.5"
                >
                  <Plus size={14} /> Add Log
                </button>
                <button onClick={() => setShowLessonModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6">
              {/* Search Input */}
              <div className="relative max-w-md">
                <input 
                  type="text" 
                  value={lessonSearchQuery}
                  onChange={e => setLessonSearchQuery(e.target.value)}
                  placeholder="Search by date or topics..."
                  className="w-full pl-12 pr-6 py-3.5 rounded-2xl bg-gray-50 border border-gray-200/50 shadow-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-gray-800 placeholder:text-gray-400 transition-all text-sm"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              </div>

              {/* Table of Logs */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-36">Date</th>
                        <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-40">Recorded By</th>
                        <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest">Topics Covered</th>
                        <th className="px-6 py-4 text-xs font-black text-gray-400 uppercase tracking-widest w-28 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredLessons.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="py-16 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center text-gray-300 mb-3">
                                <BookOpen size={20} />
                              </div>
                              <p className="text-gray-400 font-black uppercase tracking-widest text-[10px]">No logs found</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredLessons.map((lesson) => (
                          <tr 
                            key={lesson.id} 
                            onClick={() => handleLessonRowClick(lesson)}
                            className="hover:bg-gray-50/60 transition-colors cursor-pointer group"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900">
                              {new Date(lesson.lesson_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 rounded-full bg-indigo-50 flex items-center justify-center text-[9px] font-black text-indigo-600 uppercase">
                                  {(lesson.creator_name || 'T').charAt(0)}
                                </div>
                                <span className="text-xs font-bold text-gray-700">{lesson.creator_name || 'Teacher'}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-xs font-medium text-gray-500 line-clamp-1 leading-relaxed max-w-xs">
                                {lesson.content}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-1.5">
                                <button 
                                  onClick={() => handleLessonRowClick(lesson)}
                                  className="p-1.5 bg-gray-50 hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 rounded-lg transition-colors border border-gray-100 hover:border-indigo-100 shadow-sm"
                                  title="View Log Details"
                                >
                                  <Eye size={13} />
                                </button>
                                <button 
                                  onClick={(e) => handleLessonEditClick(lesson, e)}
                                  className="p-1.5 bg-gray-50 hover:bg-amber-50 text-gray-400 hover:text-amber-600 rounded-lg transition-colors border border-gray-100 hover:border-amber-100 shadow-sm"
                                  title="Edit Log"
                                >
                                  <Edit2 size={13} />
                                </button>
                                <button 
                                  onClick={(e) => handleLessonDelete(lesson.id, e)}
                                  className="p-1.5 bg-gray-50 hover:bg-rose-50 text-gray-400 hover:text-rose-600 rounded-lg transition-colors border border-gray-100 hover:border-rose-100 shadow-sm"
                                  title="Delete Log"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Sub-Modal */}
      <AnimatePresence>
        {showLessonFormModal && (
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl max-w-md w-full p-8 relative overflow-hidden text-left"
            >
              <button 
                onClick={() => setShowLessonFormModal(false)}
                className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 hover:text-rose-600 shadow-sm border border-gray-100/50 z-10 transition-all"
              >
                <X size={20} />
              </button>

              <h3 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-2 h-5 bg-indigo-600 rounded-full" />
                {editingLessonId ? 'Modify Class Log' : 'Add Class Log'}
              </h3>

              <form onSubmit={handleLessonSave} className="space-y-5">
                <div>
                  <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">Date</label>
                  <input 
                    type="date" 
                    value={lessonFormPayload.lesson_date} 
                    onChange={e => setLessonFormPayload({...lessonFormPayload, lesson_date: e.target.value})} 
                    className="w-full px-5 py-3.5 rounded-2xl bg-gray-50 focus:bg-white border border-transparent focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 font-bold text-gray-800 text-sm transition-all"
                    required 
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">What was taught?</label>
                  <textarea 
                    value={lessonFormPayload.content} 
                    onChange={e => setLessonFormPayload({...lessonFormPayload, content: e.target.value})} 
                    className="w-full px-5 py-3.5 rounded-2xl bg-gray-50 focus:bg-white border border-transparent focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 font-medium text-gray-750 placeholder:text-gray-300 min-h-[120px] text-sm transition-all"
                    placeholder="Describe the topics covered today..."
                    required
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <button 
                    type="submit" 
                    disabled={savingLesson}
                    className="flex items-center gap-2 px-8 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-indigo-100 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Check size={16} /> {editingLessonId ? 'Save' : 'Add Log'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Enroll Students Modal */}
      <AnimatePresence>
        {showEnrollModal && (
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
            >
              <div className="bg-emerald-600 p-6 text-white flex justify-between items-center shrink-0">
                <div>
                  <h2 className="text-xl font-black flex items-center gap-2"><UserPlus size={20} /> Enroll Students</h2>
                  <p className="text-emerald-100 text-xs mt-1 font-bold uppercase tracking-widest">{enrollClass?.name} · {enrollClass?.preparation_type}</p>
                </div>
                <button onClick={() => setShowEnrollModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors"><X size={22} /></button>
              </div>

              {enrollLoading ? (
                <div className="flex-1 flex items-center justify-center py-20">
                  <div className="w-10 h-10 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin" />
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto p-6 space-y-6">

                  {/* Eligible Students */}
                  <div>
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <UserPlus size={14} className="text-emerald-500" />
                      Eligible Students ({eligibleStudents.length})
                      <span className="text-gray-300">— selected {selectedToEnroll.length} preparation type in admission form</span>
                    </h3>
                    {eligibleStudents.length === 0 ? (
                      <p className="text-sm text-gray-400 italic py-4 text-center">No eligible students found. Students must select <strong>{enrollClass?.preparation_type}</strong> in their admission form and not already be enrolled in another {enrollClass?.preparation_type} class.</p>
                    ) : (
                      <div className="space-y-2">
                        {eligibleStudents.map(s => (
                          <label key={s.id} className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-emerald-50 rounded-xl cursor-pointer border border-transparent hover:border-emerald-100 transition-all">
                            <input
                              type="checkbox"
                              checked={selectedToEnroll.includes(s.id)}
                              onChange={e => setSelectedToEnroll(prev =>
                                e.target.checked ? [...prev, s.id] : prev.filter(id => id !== s.id)
                              )}
                              className="w-4 h-4 text-emerald-600 rounded"
                            />
                            <StudentAvatar photoUrl={s.photo_url} fullName={s.full_name} className="w-8 h-8 rounded-lg" />
                            <div className="flex-1">
                              <p className="text-sm font-bold text-gray-900">{s.full_name}</p>
                              <p className="text-[10px] text-gray-400 font-bold">{s.phone || 'No phone'}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    {selectedToEnroll.length > 0 && (
                      <button
                        onClick={handleEnrollSubmit}
                        className="mt-4 w-full py-3 bg-emerald-600 text-white font-black rounded-xl hover:bg-emerald-700 transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                      >
                        <UserPlus size={16} /> Enroll {selectedToEnroll.length} Student{selectedToEnroll.length > 1 ? 's' : ''}
                      </button>
                    )}
                  </div>

                  {/* Already Enrolled */}
                  <div>
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Users size={14} className="text-indigo-500" />
                      Enrolled Students ({enrolledStudents.length})
                    </h3>
                    {enrolledStudents.length === 0 ? (
                      <p className="text-sm text-gray-400 italic py-4 text-center">No students enrolled yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {enrolledStudents.map(s => (
                          <div key={s.id} className="flex items-center gap-3 p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                            <StudentAvatar photoUrl={s.photo_url} fullName={s.full_name} className="w-8 h-8 rounded-lg" />
                            <div className="flex-1">
                              <p className="text-sm font-bold text-gray-900">{s.full_name}</p>
                              <p className="text-[10px] text-gray-400 font-bold">{s.phone || 'No phone'}</p>
                            </div>
                            <button
                              onClick={() => handleRemoveFromClass(s.id)}
                              className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                              title="Remove from class"
                            >
                              <UserMinus size={15} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Log Detail Sub-Modal */}
      <AnimatePresence>
        {showLessonDetailModal && selectedLessonForDetail && (
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl max-w-lg w-full overflow-hidden relative flex flex-col max-h-[80vh] text-left"
            >
              <button 
                onClick={() => setShowLessonDetailModal(false)}
                className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 hover:text-rose-600 shadow-sm border border-gray-100/50 z-10 transition-all"
              >
                <X size={20} />
              </button>

              <div className="p-6 md:p-8 border-b border-gray-50 bg-indigo-50/20 relative">
                <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-600 rounded-lg text-[9px] font-black uppercase tracking-wider border border-indigo-100/50 mb-3 inline-block">
                  {activeClassForLesson?.name}
                </span>
                <h3 className="text-xl md:text-2xl font-black text-gray-955 tracking-tight leading-tight mb-3 pr-10">
                  Class Log Details
                </h3>

                <div className="flex flex-wrap items-center gap-4 text-[10px] font-bold text-gray-400">
                  <div className="flex items-center gap-1">
                    <Calendar className="text-indigo-500" size={12} />
                    {new Date(selectedLessonForDetail.lesson_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="text-indigo-500" size={12} />
                    By {selectedLessonForDetail.creator_name || 'Teacher'}
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-8 overflow-y-auto flex-1">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap font-medium text-sm">
                  {selectedLessonForDetail.content}
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClassManagement;
